import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enTranslations from "./langs/en.json";
import frTranslations from "./langs/fr.json";

export const SUPPORTED_LANGUAGES = ["en", "fr"];

// Keep the served markup's lang attribute in step with the active locale, so
// screen readers and search engines see the language actually on screen.
const syncDocumentLang = (lng) => {
	if (typeof document !== "undefined" && lng) {
		document.documentElement.setAttribute("lang", lng);
	}
};

// Only an explicit click on FR/EN is remembered. Auto-detected languages are
// never stored: the detector used to cache its first guess forever, so a
// visitor whose first visit came out English stayed English for good.
export const CHOICE_KEY = "pp-lang-choice";

const FRENCH_TIMEZONES =
	/^(Europe\/(Paris|Brussels|Luxembourg|Monaco)|America\/(Montreal|Toronto)|Indian\/Reunion|America\/(Martinique|Guadeloupe|Cayenne)|Pacific\/(Noumea|Tahiti))$/;

/**
 * Pick the language for a visitor who has not chosen one:
 * French if French is anywhere in the browser's list (French users often run
 * an English OS with French second), or if the timezone is French-speaking;
 * otherwise the browser's first supported language; otherwise English.
 */
export function detectLanguage({ languages = [], timeZone = "" } = {}) {
	const codes = languages.map((l) => String(l).toLowerCase().slice(0, 2));
	if (codes.includes("fr")) return "fr";
	if (FRENCH_TIMEZONES.test(timeZone)) return "fr";
	return codes.find((c) => SUPPORTED_LANGUAGES.includes(c)) || "en";
}

const choiceDetector = {
	name: "choiceOrSignals",
	lookup() {
		try {
			const chosen = localStorage.getItem(CHOICE_KEY);
			if (SUPPORTED_LANGUAGES.includes(chosen)) return chosen;
		} catch {}
		let timeZone = "";
		try {
			timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
		} catch {}
		const languages =
			typeof navigator !== "undefined"
				? [...(navigator.languages || [navigator.language])]
				: [];
		return detectLanguage({ languages, timeZone });
	},
	cacheUserLanguage() {},
};

const detector = new LanguageDetector();
detector.addDetector(choiceDetector);

i18n.use(detector)
	.use(initReactI18next)
	.init({
		resources: {
			en: { translation: enTranslations },
			fr: { translation: frTranslations },
		},
		supportedLngs: SUPPORTED_LANGUAGES,
		fallbackLng: "en",
		load: "languageOnly",
		detection: {
			order: ["choiceOrSignals"],
			caches: [],
		},
		debug: false,
		interpolation: {
			escapeValue: false,
		},
	});

syncDocumentLang(i18n.resolvedLanguage);
i18n.on("languageChanged", syncDocumentLang);

export default i18n;
